/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useEffect, useState } from 'react';  
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Stack, IconButton, Box, Alert, Select, MenuItem, FormControl, Paper } from '@mui/material';
import { FiPlus, FiX, FiTrash2 } from 'react-icons/fi';
import { assignUserToDataset, deassignUserFromDataset, fetchAssignedUsers, updatePermissionDataset, getDatasetOrganization, updateDatasetOrganization } from './queries';
import { BsPerson } from 'react-icons/bs';
import { extractDatasetName, getDatasetPermission } from './DatasetHelper';

export const ShareDatasetDialog = ({
  datasetToShare,
  open,
  datasetPermissions,
  setOpen,
  default_data,
  getToken,
  getUser
}) => {
  const [email, setEmail] = useState('');
  const [emails, setEmails] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const [queryError, setQueryError] = useState(undefined);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [permissionChanges, setPermissionChanges] = useState([]);
  const [organizationSharingEnabled, setOrganizationSharingEnabled] = useState(-1);

  const user = getUser();
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/;

  const handleSubmit = () => {
    const emailsCopy = [...emails];
    if(email.length > 0) {
      if (isEmailValid(email)) {
        emailsCopy.push(email);
        setEmail('');
        setEmails(emailsCopy);
      }
    }
    setLoading(true);
    setQueryError(undefined);
    var error_txt = '';
    emailsCopy.forEach((email) => {
      var assign_obj = assignUserToDataset(datasetToShare, email, default_data, getToken);
      if(assign_obj === undefined || assign_obj === null) {
        error_txt += `${email}: Failed to share dataset\n`;
        setQueryError(error_txt);
      }
      else {
        assign_obj.then((response) => {
          if (response.status === 200) {
            setMessage('Dataset shared successfully');
            setEmails(emails.filter((e) => e !== email));
            setAssignedUsers([...assignedUsers, email]);
          } else {
            error_txt += `${email}: Failed to share dataset - ${response.response}\n`;
            setQueryError(error_txt);
          }
        }
      )
    }
    })
    setLoading(false);
  }

  const deassignUser = (email) => {
    var deassign_obj = deassignUserFromDataset(datasetToShare, email, default_data, getToken);
    if(deassign_obj === undefined || deassign_obj === null) {
      setQueryError(`${email}: Failed to remove user from dataset`);
    }
    else {
      deassign_obj.then((response) => {
        if (response.status === 200) {
          setAssignedUsers(assignedUsers.filter((user) => user !== email));
          setMessage('User removed successfully');
          setQueryError(undefined);
        } else {
          setQueryError(`${email}: Failed to remove user from dataset - ${response.response}`);
        }
      }
    )}
  }

  const getAssignedUsers = () => {
    setPermissionChanges([]);
    setEmails([]);
    setQueryError(undefined);
    setAssignedUsers([]);
    setMessage('');
    setError(undefined);

    var assigned_obj = fetchAssignedUsers(datasetToShare, default_data, getToken);
    if(assigned_obj === undefined || assigned_obj === null) {
      setQueryError('Failed to fetch assigned users');
    }
    else {
      assigned_obj.then((response) => {
        if (response.status === 200) {
          const data_array = response.response;
          const users = data_array.map((item) => item.email);
          setAssignedUsers(users);
          const initialPermissions = users.reduce((acc, user) => {
            // get permissions from data_array with email
            const user_obj = data_array.find((item) => item.email === user);
            if (user_obj) {
              acc[user] = user_obj.permissions;
            } else {
              acc[user] = 'read'; // Default permission
            }
            return acc;
          }, {});
          setPermissions(initialPermissions);
        } else {
          setQueryError(`Failed to fetch assigned users - ${response.response}`);
        }
      })
      .catch((error) => {
        setQueryError(`An error occurred while fetching assigned users - ${error.message}`);
      });
    }
  };

  const isEmailValid = (email) => {  
    if (emailPattern.test(email)) {
      return true;
    } else {
      setError('Invalid email address');
      return false;
    }
  }

  const handlePermissionChange = (email, permission) => {
    // Update permissions state
    setPermissions({
      ...permissions,
      [email]: permission,
    });

    // Update permissionChanges state
    setPermissionChanges((prevChanges) => {
      const existingChangeIndex = prevChanges.findIndex((change) => change.email === email);
      if (existingChangeIndex !== -1) {
        // Update existing change
        const updatedChanges = [...prevChanges];
        updatedChanges[existingChangeIndex].permission = permission;
        return updatedChanges;
      } else {
        // Add new change
        return [...prevChanges, { email, permission }];
      }
    });
  };

  const handleUpdatePermissions = () => {
    setLoading(true);
    var error_txt = '';
    permissionChanges.forEach(({ email, permission }) => {
      var update_obj = updatePermissionDataset(datasetToShare, email, permission, default_data, getToken);
      if(update_obj === undefined || update_obj === null) {
        error_txt += `${email}: Failed to update permission\n`;
        setQueryError(error_txt);
      }
      else {
        update_obj.then((response) => {
          if (response.status !== 200) {
            error_txt += `${email}: Failed to update permission - ${response.response}\n`;
            setQueryError(error_txt);
          }
        })
        .catch((error) => {
          error_txt += `${email}: An error occurred while updating permissions - ${error.message}\n`;
          setQueryError(error_txt);
        });
      }
    });
    setLoading(false);
    setPermissionChanges([]);
  };

  const handleOrganizationSharing = (enable) => {
    setLoading(true);
    setQueryError(undefined);
    var update_obj = updateDatasetOrganization(datasetToShare, enable, default_data, getToken);
    if(update_obj === undefined || update_obj === null) {
      setQueryError(`Failed to ${enable ? 'enable' : 'disable'} organization sharing`);
    }
    else {
      update_obj.then((response) => {
        if (response.status === 200) {
          setOrganizationSharingEnabled(enable);
          setMessage(`Organization sharing ${enable ? 'enabled' : 'disabled'} successfully`);
        } else {
          setQueryError(`Failed to ${enable ? 'enable' : 'disable'} organization sharing - ${response.response}`);
        }
      })
      .catch((error) => {
        setQueryError(`An error occurred while ${enable ? 'enabling' : 'disabling'} organization sharing - ${error.message}`);
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if(assignedUsers.length === 0) {
      getAssignedUsers();
    }
    if(user.organization_id > 0) {
      var org_obj = getDatasetOrganization(datasetToShare, default_data, getToken);
      if(org_obj === undefined || org_obj === null) {
        setQueryError('Failed to fetch organization sharing status');
      }
      else {
        org_obj.then((response) => {
          if (response.status === 200) {
            setOrganizationSharingEnabled(response.response === 1);
          } else {
            setQueryError(`Failed to fetch organization sharing status - ${response.response}`);
          }
        })
        .catch((error) => {
          setQueryError(`An error occurred while fetching organization sharing status - ${error.message}`);
        });
      }
    }
  }, [datasetToShare])

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Share Dataset {extractDatasetName(datasetToShare)}</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
        {assignedUsers.length > 0 && <Stack>
          <Typography sx={{marginBottom: '10px'}}>Assigned Users:</Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Read: Can query the dataset<br />
            Edit: Can query and upload files to the dataset<br />
            Admin: Can query, upload files, and share the dataset
          </Alert>
          {user.organization_id > 0 && organizationSharingEnabled !== -1 && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Button
                variant='contained'
                onClick={() => handleOrganizationSharing(!organizationSharingEnabled)}
                color="primary"
                disabled={loading}
              >
                {organizationSharingEnabled ? 'Stop Sharing with Organization' : 'Share with Entire Organization'}
              </Button>
            </Box>
          )}
            <Paper sx={{ padding: 2, backgroundColor: 'white', color: 'black' }}>
            <Stack spacing={0.5}>
              {assignedUsers.map((userEmail, index) => (
                <Box key={index} display="flex" alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ flex: '0 0 50%', wordWrap: 'break-word' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <BsPerson />
                      <Typography sx={{fontSize: '11pt'}}>{userEmail}</Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: '0 0 33.33%' }}>
                    {userEmail !== user.email ?
                      <FormControl size="small" fullWidth>
                        { getDatasetPermission(datasetToShare, datasetPermissions) === 'owner' && 
                        <Select
                          value={permissions[userEmail] || 'read'}
                          onChange={(e) => handlePermissionChange(userEmail, e.target.value)}
                          sx={{ color: 'black', borderColor: 'black', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' } }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                bgcolor: 'white',
                                '& .MuiMenuItem-root': {
                                  color: 'black',
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="read" sx={{ color: 'black' }}>Read</MenuItem>
                          <MenuItem value="edit" sx={{ color: 'black' }}>Edit</MenuItem>
                          <MenuItem value="admin" sx={{ color: 'black' }}>Admin</MenuItem>
                        </Select>}
                      </FormControl>
                    : getDatasetPermission(datasetToShare, datasetPermissions) === 'owner' ? <Typography sx={{marginLeft: '10px'}}>Owner</Typography> : ''}
                  </Box>
                  <Box sx={{ flex: '0 0 16.67%', textAlign: 'center' }}>
                    {userEmail !== user.email && (
                      <IconButton
                        aria-label='remove email'
                        size='small'
                        color="error"
                        onClick={() => deassignUser(userEmail)}
                      >
                        <FiTrash2/>
                      </IconButton>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
            <Box display="flex" justifyContent="center" mt={2}>
              {permissionChanges.length > 0 && (
                <Button
                variant='contained' 
                onClick={handleUpdatePermissions} 
                color="primary" 
                disabled={loading || permissionChanges.length === 0}
                sx={{
                  backgroundColor: '#393939',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#393939',
                  },
                }}
              >
                Update
              </Button>)}
            </Box>
          </Paper>
          </Stack>}
          <Typography>Enter the email addresses of the people you would like to share this dataset with:</Typography>
          {queryError && <Alert severity='error'>{queryError}</Alert>}
          {message && <Alert severity='success'>{message}</Alert>} 
          {emails.length > 0 && ( <Typography>Users to add:</Typography> )}
          {emails.map((email, index) => (
            <Stack key={index} direction={'row'} spacing={0.5} alignItems={'center'}>
              <BsPerson />
              <Typography>{email}</Typography>
              <IconButton
                aria-label='remove email'
                size='small'
                color="error"
                onClick={() => setEmails(emails.filter((e) => e !== email))}
              >
                <FiX/>
              </IconButton>
            </Stack>
          ))} 
        </Stack>
        <Stack spacing={2} direction={'row'} mt={3} justifyContent={'top'}>
          <TextField
            label="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value.toLowerCase());
              setError(undefined)
            }}
            size='small'
            error={error !== undefined}
            helperText={error}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (isEmailValid(email)) {
                  setEmails([...emails, email.toLowerCase()]);
                  setEmail('');
                }
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              let pastedText = e.clipboardData.getData('text');
              if (!pastedText) return;

              pastedText = pastedText.replace(/\\n/g, '\n');
              pastedText = pastedText.replace(/\r/g, '').replace(/\\r/g, '');
              
              const splitted = pastedText
                .split(/[\r\n,]+/)
                .map(email => email.trim())
                .filter(Boolean);
              
              if (!splitted.length) return;
          
              const uniqueEmailPasted = new Set();
              const invalidEmails = [];
              splitted.forEach((email) => {
                if (isEmailValid(email)) {
                  uniqueEmailPasted.add(email.toLowerCase());
                } else {
                  console.error("Invalid Email", email)
                  invalidEmails.push(email)
                }
              });

              const uniqueEmails = Array.from(uniqueEmailPasted).filter((em) => !emails.includes(em));
          
              if (uniqueEmails.length > 0) {
                setEmails((prev) => [...prev, ...uniqueEmails]);
              }
            }}
            fullWidth
          />
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'flex-start', marginTop: '6px !important' }}>
            <IconButton
              aria-label='add email'
              size='small'
              sx={{
                height: '100%',
                alignSelf: 'flex-start',
              }}
              onClick={()=> {
                if (isEmailValid(email)) {
                  setEmails([...emails, email.toLowerCase()]);
                  setEmail('');
                }
              }}
            >
              <FiPlus />
            </IconButton>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={handleSubmit} disabled={loading}>
          Share
        </Button>
        <Button onClick={() => setOpen(false)} color="secondary" disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
} 